/**
 * Common translations: common, help, keyboard, password, notifications, breadcrumbs, quickActions, globalSearch, ticket, validation, pageTitles, errors
 */

export const common = {
  notFoundPage: {
    title: 'Seite nicht gefunden',
    description: 'Diese Seite gibt es nicht (mehr). Vielleicht hat sich die Adresse geändert.',
    backHome: 'Zur Startseite'
  },
  // BCP-47 locale tag — read by toLocaleDateString and similar APIs that
  // need a per-locale identifier rather than the bare i18n locale code.
  locale: 'de-DE',
  save: 'Speichern',
  cancel: 'Abbrechen',
  delete: 'Löschen',
  confirm: 'Bestätigen',
  done: 'Fertig',
  edit: 'Bearbeiten',
  add: 'Hinzufügen',
  back: 'Zurück',
  next: 'Weiter',
  loading: 'Wird geladen...',
  error: 'Fehler',
  success: 'Erfolgreich',
  important: 'Wichtig',
  close: 'Schließen',
  search: 'Suchen',
  filter: 'Filtern',
  reset: 'Zurücksetzen',
  retry: 'Erneut versuchen',
  freeForever: 'Dauerhaft kostenlos',
  upgradeAnytime: 'Jederzeit auf bezahlten Tarif upgradebar.',
  yes: 'Ja',
  no: 'Nein',
  or: 'oder',
  all: 'Alle',
  none: 'Keine',
  optional: 'Optional',
  required: 'Pflichtfeld',
  active: 'Aktiv',
  creating: 'Wird erstellt...',
  settings: 'Einstellungen',
  copied: 'Kopiert',
  refresh: 'Aktualisieren',
  justNow: 'gerade eben',
  minutesAgo: 'vor {n}m',
  hoursAgo: 'vor {n}h',
  daysAgo: 'vor {n}d',
  viewAll: 'Alle anzeigen',
  termPrice: {
    period: {
      month: 'Monat',
      quarter: 'Quartal',
      halfYear: 'Halbjahr',
      year: 'Jahr'
    },
    monthlyEquivalent: 'entspricht {price} / Monat'
  },
  // The billing row of a plan and the dialog that changes its period for the
  // next renewal.
  billingPeriod: {
    label: 'Abrechnungszeitraum',
    periods: {
      monthly: 'Monatlich',
      quarterly: 'Vierteljährlich',
      halfAnnually: 'Halbjährlich',
      annually: 'Jährlich'
    },
    renewsOn: 'Verlängert sich am {date}',
    hourly: 'Stündlich abgerechnet, höchstens zum Monatspreis je Kalendermonat',
    scheduled: 'Ab dem {date}: {period}',
    change: 'Zeitraum ändern',
    withdraw: 'Änderung zurücknehmen',
    withdrawn: 'Die Änderung des Abrechnungszeitraums ist zurückgenommen.',
    dialog: {
      title: 'Abrechnungszeitraum ändern',
      current: 'Aktueller Zeitraum: {period}',
      effective: 'Die Änderung wird mit dem Ende deines laufenden Abrechnungszeitraums am {date} wirksam. Ab dann gilt der neue Zeitraum, und an ihn schließt sich jeweils ein Zeitraum gleicher Länge an. Bis dahin kannst du die Änderung hier zurücknehmen.',
      price: '{price} je {period}',
      hourlyPrice: 'stündlich abgerechnet, höchstens {price} je Monat',
      from: 'ab dem {date}',
      scheduled: 'Vorgemerkt ist bereits: ab dem {date} {period}.',
      none: 'Für diesen Vertrag gibt es keinen anderen Abrechnungszeitraum.',
      confirm: 'Zeitraum ändern',
      cancel: 'Abbrechen',
      success: 'Der Wechsel des Abrechnungszeitraums ist vorgemerkt.'
    }
  },
  paymentErrors: {
    declined: 'Die Zahlung konnte nicht abgebucht werden. Es wurde nichts berechnet. Bitte prüfe deine Zahlungsmethode oder hinterlege eine andere.',
    authenticationRequired: 'Deine Bank verlangt für diese Zahlung eine Bestätigung, die ohne dich nicht möglich ist. Es wurde nichts berechnet. Bitte hinterlege eine andere Zahlungsmethode.'
  },
  termSwitch: {
    keepsTerm: 'Dein bezahlter Zeitraum bleibt und läuft bis zum {date}. Ab dem nächsten Zeitraum kostet dein Plan {newPrice} je {period}.',
    charge: 'Für die Zeit von heute bis zum {date} berechnen wir dir jetzt den Unterschied von {amount} über deine hinterlegte Zahlungsmethode. Der Wechsel erfolgt, sobald die Zahlung eingegangen ist.',
    refund: 'Für die Zeit von heute bis zum {date} erstatten wir dir den Unterschied von {amount} innerhalb von 14 Tagen nach dem Wechsel auf das ursprünglich verwendete Zahlungsmittel.',
    refundSepa: 'Hast du per SEPA-Lastschrift bezahlt, erstatten wir diesen Teil innerhalb von 14 Tagen, nachdem die achtwöchige Frist abgelaufen ist, in der deine Bank die Lastschrift noch zurückbuchen kann.',
    leaveRefund: 'Dein bisheriger Plan endet mit dem Wechsel. Die bezahlte Zeit danach erstatten wir dir taggenau anteilig, {amount}, innerhalb von 14 Tagen nach dem Wechsel auf das ursprünglich verwendete Zahlungsmittel. Pay-as-you-go rechnen wir monatlich nach Nutzung ab.',
    nothingDue: 'Für den laufenden Zeitraum fällt jetzt kein Betrag an.',
    // Monthly switches. A Kubernetes block is these sentences in order:
    // charge, refund when the old period was paid, usageBefore when leaving
    // pay-as-you-go, next, openEnded.
    monthly: {
      k8sCharge: 'Du wechselst den Namespace {namespace} von {oldPlan} zu {newPlan}. Der Wechsel gilt sofort. Für den Rest des laufenden Monats bis zum {monthEnd} berechnen wir dir jetzt {amount} {vat} für {newPlan}.',
      k8sChargeBase: 'Du wechselst den Namespace {namespace} von {oldPlan} zu {newPlan}. Der Wechsel gilt sofort. Für den Rest des laufenden Monats bis zum {monthEnd} berechnen wir dir jetzt {amount} {vat} als Grundentgelt für {newPlan}.',
      k8sRefund: 'Das für {oldPlan} bereits gezahlte Entgelt für die Zeit vom Wechsel bis zum {oldPaidEnd} von {refund} erstatten wir dir innerhalb von 14 Tagen nach dem Wechsel auf das ursprünglich verwendete Zahlungsmittel.',
      k8sRefundBase: 'Das für {oldPlan} bereits gezahlte Grundentgelt für die Zeit vom Wechsel bis zum {oldPaidEnd} von {refund} erstatten wir dir innerhalb von 14 Tagen nach dem Wechsel auf das ursprünglich verwendete Zahlungsmittel.',
      k8sUsageBefore: 'Deine Nutzung bis zum Wechsel rechnen wir mit der nächsten Rechnung zu den bisherigen Nutzungspreisen ab.',
      k8sNext: 'Ab dem {nextFirst} kostet {newPlan} {newPrice} je Monat {vat}.',
      k8sNextBase: 'Ab dem {nextFirst} kostet {newPlan} {basePrice} Grundentgelt je Monat {vat}. Deine Nutzung ab dem Wechsel rechnen wir monatlich nach Verbrauch ab: {usagePrices} {vat}.',
      openEnded: 'Der Vertrag läuft unbefristet weiter, und du kannst ihn jederzeit mit einer Frist von einem Monat kündigen.',
      hosting: 'Du wechselst dein Webhosting-Paket {identifier} von {oldPlan} zu {newPlan}. Der Wechsel gilt sofort. Ab dem nächsten Abrechnungsmonat kostet {newPlan} {newPrice} je Monat {vat}. Für den Rest des laufenden Monats bis zum {periodEnd} berechnen wir dir den Unterschied von {amount} {vat} mit deiner nächsten Rechnung; heute wird nichts abgebucht. Der Vertrag läuft unbefristet weiter, und du kannst ihn jederzeit mit einer Frist von einem Monat kündigen.'
    },
    freeToPaid: {
      intro: 'Du wechselst den Namespace {namespace} vom kostenlosen Tarif in {newPlan}.',
      prices: 'Preise {vat}: {usagePrices}{basePriceSentence}. Wir rechnen monatlich nach der tatsächlichen Nutzung ab; eine Mindestlaufzeit gibt es nicht. Du kannst den Vertrag jederzeit mit einer Frist von einem Monat kündigen.',
      basePriceSentence: ', zuzüglich {basePrice} je Monat',
      chargeNow: 'Für den Rest des laufenden Monats bis zum {monthEnd} berechnen wir dir jetzt {amount} {vat} als Grundentgelt für {newPlan} über deine hinterlegte Zahlungsmethode; ab dem {nextFirst} kostet {newPlan} {basePrice} Grundentgelt je Monat {vat}.'
    },
    usagePrices: '{cpuPrice} je {cpuUnit}, {ramPrice} je {ramUnit}, {storagePrice} je GB Speicher und Monat, {egressPrice} je GB ausgehendem Datenverkehr',
    units: {
      cpu: 'Core-Stunde',
      ram: 'GiB-Stunde'
    },
    vat: {
      gross: '(inkl. MwSt.)',
      net: '(zzgl. MwSt.)'
    },
    switchWithdrawalNote: 'Diesen Wechsel kannst du als Verbraucher 14 Tage lang widerrufen. Dein bisheriger Plan kommt dann zurück, und dein bestehender Vertrag bleibt unberührt. Weil du verlangst, dass wir den Wechsel sofort ausführen, zahlst du bei einem Widerruf für die Zeit bis dahin den Preis des neuen Plans anteilig; was du für diese Zeit schon für den alten Plan gezahlt hast, wird angerechnet. Den Rest des heute berechneten Betrags erstatten wir dir innerhalb von 14 Tagen. {link}',
    switchWithdrawalNoteNextInvoice: 'Diesen Wechsel kannst du als Verbraucher 14 Tage lang widerrufen. Dein bisheriger Plan kommt dann zurück, und dein bestehender Vertrag bleibt unberührt. Die Preisdifferenz für den Wechsel kommt mit deiner nächsten Rechnung. Weil du verlangst, dass wir den Wechsel sofort ausführen, berechnen wir dir bei einem Widerruf davon nur den Anteil für die Zeit bis dahin. {link}',
    switchWithdrawalNoteFreeToPaid: 'Diesen Wechsel kannst du als Verbraucher 14 Tage lang widerrufen; dein Namespace kehrt dann in den kostenlosen Tarif zurück. Weil du verlangst, dass wir den Wechsel sofort ausführen, zahlst du bei einem Widerruf für die Zeit bis dahin das anteilige Grundentgelt und deine Nutzung zu den oben genannten Preisen; das heute berechnete Grundentgelt rechnen wir darauf an und erstatten den Rest innerhalb von 14 Tagen. {link}',
    switchWithdrawalLink: 'Widerrufsbelehrung',
    switchWithdrawalConsent: 'Ich verlange ausdrücklich, dass Sie den Wechsel vor Ablauf der Widerrufsfrist ausführen und mit der Leistung im neuen Tarif beginnen. Mir ist bekannt, dass mein Widerrufsrecht erlischt, sobald Sie die Leistung vollständig erbracht haben.',
    confirmPaid: 'Zahlungspflichtig wechseln',
    // The line for an owner on the current terms, the box
    // for one with terms to accept, its document list cut to what the terms
    // version covers. The comparison sentence is left out where no comparison
    // can be linked.
    terms: {
      line: 'Für diesen Wechsel gelten unsere {agb} und Service-Vereinbarungen in der Fassung vom {version}.',
      holder: 'Ich akzeptiere die neue Fassung unserer Vertragsbedingungen vom {version} ({agb} und Service-Vereinbarungen) für alle Verträge meines Kontos.',
      contract: 'Ich akzeptiere die neue Fassung unserer Vertragsbedingungen vom {version} ({agb} und Service-Vereinbarungen) für diesen Vertrag.',
      compare: 'Was sich geändert hat, zeigt die {link}.',
      agbLink: 'AGB',
      compareLink: 'Gegenüberstellung'
    },
    pendingTitle: 'Wechsel eingeleitet',
    pending: 'Deine SEPA-Lastschrift wird noch eingezogen. Der Wechsel erfolgt, sobald sie eingegangen ist. Schlägt der Einzug fehl, bleibt dein bisheriger Tarif bestehen, und es wird nichts berechnet.',
    resources: {
      cpu: 'CPU',
      memory_gib: 'Arbeitsspeicher',
      pvcs: 'Volumes',
      storage_gib: 'Speicher',
      other: 'Ressourcen'
    },
    errors: {
      noPaymentMethod: 'Für den Wechsel brauchst du eine hinterlegte Zahlungsmethode. Es wurde nichts berechnet und nichts gewechselt.',
      paymentFailed: 'Die Zahlung wurde abgelehnt. Es wurde nichts berechnet und nichts gewechselt. Bitte prüfe deine Zahlungsmethode.',
      authenticationRequired: 'Deine Bank verlangt für diese Zahlung eine Bestätigung, die ohne dich nicht möglich ist. Es wurde nichts berechnet und nichts gewechselt. Bitte hinterlege eine andere Zahlungsmethode.',
      quoteStale: 'Der Betrag hat sich inzwischen geändert. Bitte prüfe den neuen Betrag und bestätige erneut.',
      quotaExceeded: 'Dein Namespace nutzt mehr {resource}, als der neue Plan erlaubt: {used} bei {limit}. Bitte reduziere die Nutzung und versuche es erneut.',
      inProgress: 'Für diesen Plan läuft gerade ein Wechsel. Bitte warte, bis er abgeschlossen ist.',
      cancellationPending: 'Dieser Plan ist gekündigt. Nimm die Kündigung zurück, um ihn zu wechseln.',
      paymentOverdue: 'Für diesen Plan ist eine Zahlung offen. Bitte begleiche sie, bevor du wechselst.',
      wertersatzUnpaid: 'Aus einem früheren Widerruf ist noch ein Wertersatz offen. Bitte begleiche die Rechnung dazu, bevor du wechselst.',
      withdrawalSettling: 'Ein früherer Widerruf ist noch nicht abgerechnet, weil Zahlungen dazu noch in Bearbeitung sind.',
      withdrawn: 'Für diesen Vertrag ist bereits ein Widerruf eingegangen.',
      planChanged: 'Dein Plan hat sich gerade geändert. Bitte lade die Seite neu und versuche es erneut.',
      unavailable: 'Der Wechsel ist gerade nicht möglich. Bitte versuche es später erneut oder wende dich an den Support.',
      unconfirmed: 'Wir konnten die Zahlung noch nicht bestätigen. Bitte versuche es nicht erneut: Wird die Zahlung bestätigt, schließen wir den Wechsel automatisch ab; wird sie es nicht, erstatten wir dir einen bereits abgebuchten Betrag.',
      termsRequired: 'Für diesen Wechsel brauchen wir deine Zustimmung zur aktuellen Fassung unserer Vertragsbedingungen. Bitte setze das Häkchen und bestätige erneut. Es wurde nichts berechnet und nichts gewechselt.',
      termsAcceptanceRequired: 'Für diesen Wechsel brauchen wir zuerst deine Zustimmung zur aktuellen Fassung unserer Vertragsbedingungen. Die Zustimmung ist nur im Kundenportal möglich: Melde dich dort an, stimme zu und starte den Wechsel von dort. Es wurde nichts berechnet und nichts gewechselt.',
      consentRequired: 'Für diesen Wechsel brauchen wir deine Zustimmung, dass wir ihn vor Ablauf der Widerrufsfrist ausführen. Bitte setze das Häkchen und bestätige erneut. Es wurde nichts berechnet und nichts gewechselt.',
      notOnStripe: 'Dieser Vertrag wird noch nicht über unser Zahlungssystem abgerechnet, deshalb kannst du den Abrechnungszeitraum nicht selbst ändern. Bitte wende dich an den Support.',
      switchWindowOpen: 'Deinen letzten Tarifwechsel kannst du noch widerrufen. Den Abrechnungszeitraum kannst du ändern, sobald die Widerrufsfrist abgelaufen ist.',
      switchPaymentDue: 'Für einen Tarifwechsel deines vServers ist der Unterschiedsbetrag noch offen. Bezahle ihn oder stelle den Server in den bisherigen Tarif zurück; bis dahin ist keine andere Änderung möglich.',
      resizeInProgress: 'Dein vServer wird gerade umgestellt. Bitte warte, bis die Umstellung abgeschlossen ist.',
      periodNotStarted: 'Den Abrechnungszeitraum kannst du ändern, sobald dein vServer zum ersten Mal gelaufen ist.',
      periodChangeScheduled: 'Für diesen Vertrag ist ein Wechsel des Abrechnungszeitraums vorgemerkt. Bis er wirksam wird, sind kein Tarifwechsel und keine weitere Änderung des Zeitraums möglich. Du kannst ihn unter „Abrechnungszeitraum“ zurücknehmen.',
      periodUnavailable: 'Dieser Abrechnungszeitraum ist für deinen Vertrag nicht verfügbar.',
      mailTakeoverPending: 'Ein Webhosting-Paket übernimmt gerade E-Mail für diese Domain; der Tarif endet von selbst mit Erstattung.',
      samePeriod: 'Dein Vertrag läuft bereits mit diesem Abrechnungszeitraum.',
      noChangeScheduled: 'Für diesen Vertrag ist kein Wechsel des Abrechnungszeitraums vorgemerkt.',
      changeInEffect: 'Der Wechsel des Abrechnungszeitraums ist bereits wirksam und kann nicht mehr zurückgenommen werden.',
      subscriptionBusy: 'An diesem Vertrag wird gerade eine andere Änderung verarbeitet. Bitte versuche es in einer Minute noch einmal.',
      stripeUnavailable: 'Unser Zahlungsdienstleister ist gerade nicht erreichbar. Es wurde nichts geändert. Bitte versuche es später noch einmal.'
    }
  }
} as const

export const help = {
  title: 'Hilfe & Kontakt',
  weHelpYou: 'Wir helfen gerne weiter.'
} as const

export const keyboard = {
  title: 'Tastenkürzel',
  categories: {
    search: 'Suche',
    navigation: 'Navigation',
    general: 'Allgemein',
    dev: 'Entwicklung'
  },
  noShortcuts: 'Keine Kürzel in dieser Kategorie',
  ctrlKNote: 'Ctrl+K öffnet ebenfalls die Suche'
} as const

export const password = {
  strength: {
    veryWeak: 'sehr schwaches',
    weak: 'schwaches',
    fair: 'ausreichendes',
    good: 'gutes',
    strong: 'starkes',
    label: '{strength} Passwort'
  },
  requirements: {
    title: 'Passwort-Anforderungen',
    minLength: 'Mindestens 8 Zeichen',
    uppercase: 'Großbuchstabe (A-Z)',
    lowercase: 'Kleinbuchstabe (a-z)',
    number: 'Zahl (0-9)'
  }
} as const

export const notifications = {
  title: 'Benachrichtigungen',
  ariaLabel: 'Benachrichtigungen',
  markAllRead: 'Alle gelesen',
  empty: 'Keine Benachrichtigungen',
  categories: {
    renewal: 'Verlängerung',
    security: 'Sicherheit',
    billing: 'Rechnung',
    system: 'System',
    verification: 'Verifizierung'
  },
  saved: 'Erfolgreich gespeichert',
  updated: 'Erfolgreich aktualisiert',
  deleted: 'Erfolgreich gelöscht',
  created: 'Erfolgreich erstellt',
  addedToCart: 'Zum Warenkorb hinzugefügt',
  copiedToClipboard: 'In Zwischenablage kopiert'
} as const

export const breadcrumbs = {
  dashboard: 'Dashboard',
  admin: 'Admin',
  email: 'E-Mail',
  domains: 'Domains',
  packet: 'Pakete',
  invoice: 'Rechnungen',
  account: 'Account',
  product: 'Produkte',
  user: 'Benutzer',
  details: 'Details',
  security: 'Sicherheit'
} as const

export const quickActions = {
  title: 'Schnellaktionen',
  ariaLabel: 'Schnellaktionen',
  newTicket: 'Neues Ticket',
  orderProduct: 'Produkt bestellen',
  domains: 'Domains',
  newProduct: 'Neues Produkt',
  adminBadge: 'Admin'
} as const

export const globalSearch = {
  triggerText: 'Suchen...',
  openSearch: 'Suche öffnen',
  openSearchShortcut: 'Suche öffnen (Strg+K)',
  placeholder: 'Suchen nach Seiten, Produkten, Domains...',
  clearInput: 'Eingabe löschen',
  closeSearch: 'Suche schließen',
  noResults: 'Keine Ergebnisse für "{query}"',
  recentSearches: 'Letzte Suchen',
  clearRecent: 'Löschen',
  hints: {
    navigate: 'navigieren',
    select: 'auswählen',
    close: 'schließen'
  },
  groups: {
    domain: 'Domain',
    navigation: 'Navigation',
    product: 'Produkte'
  },
  domainResult: {
    title: 'Domain "{query}" prüfen',
    subtitle: 'Verfügbarkeit prüfen'
  },
  nav: {
    home: 'Startseite',
    kubernetes: 'Kubernetes',
    kubernetesSub: 'Container-Orchestrierung',
    vserver: 'VServer',
    vserverSub: 'Virtuelle Server',
    webhosting: 'Webhosting',
    webhostingSub: 'Hosting-Pakete',
    domains: 'Domains',
    domainsSub: 'Domain suchen & registrieren',
    products: 'Alle Produkte',
    productsSub: 'Produktübersicht',
    cart: 'Warenkorb',
    login: 'Anmelden',
    register: 'Registrieren',
    dashboard: 'Dashboard',
    dashboardSub: 'Mein Bereich',
    impress: 'Impressum',
    privacy: 'Datenschutz',
    agb: 'AGB',
    agbSub: 'Allgemeine Geschäftsbedingungen'
  }
} as const

export const ticket = {
  title: 'Support-Ticket',
  subtitle: 'Wie können wir dir helfen?',
  responseTime: 'Unser Support-Team antwortet in der Regel innerhalb von 24 Stunden.',
  loadError: 'Das Ticket-Formular konnte nicht geladen werden. Bitte versuche es später erneut oder schreibe uns direkt an support{\'@\'}itsh.dev.'
} as const

export const a11y = {
  skipToContent: 'Zum Inhalt springen',
  mainNavigation: 'Hauptnavigation',
  bottomNavigation: 'Untere Navigation',
  shoppingCart: 'Warenkorb',
  goToCart: 'Zum Warenkorb',
  logoToHome: 'ITSH - Zur Startseite'
} as const

export const validation = {
  required: 'Pflichtfeld',
  email: 'Ungültige E-Mail-Adresse',
  emailFormat: 'Falsches E-Mail Format',
  number: 'Nur Zahlen erlaubt',
  minLength: 'Mindestens {min} Zeichen erforderlich',
  maxLength: 'Maximal {max} Zeichen erlaubt',
  phone: 'Nur Ziffern erlaubt (ohne Leerzeichen)',
  postcode: 'PLZ muss 5 Ziffern haben',
  alphanumeric: 'Nur Buchstaben, Zahlen und Satzzeichen erlaubt',
  noHtml: 'HTML-Tags sind nicht erlaubt',
  invalidInput: 'Ungültige Eingabe',
  invalidPostcodeCountry: 'Die Postleitzahl passt nicht zum Format des gewählten Landes. Bitte prüfe deine Eingabe.',
  postcodeStateMismatch: 'Die Postleitzahl liegt nicht im gewählten Bundesland. Bitte prüfe deine Eingabe.',
  ipv4: 'Ungültige IPv4-Adresse',
  ipv6: 'Ungültige IPv6-Adresse',
  hostname: 'Ungültiger Hostname',
  port: 'Muss zwischen 0 und 65535 liegen',
  numeric: 'Muss eine Zahl sein',
  emailMissingAt: "E-Mail muss ein {'@'} enthalten",
  emailMissingDomain: "Bitte Domain nach {'@'} eingeben (z.B. beispiel.de)",
  emailInvalidDomain: 'Domain muss einen Punkt enthalten (z.B. beispiel.de)',
  passwordTooShort: 'Mindestens {min} Zeichen erforderlich (aktuell: {current})',
  passwordNeedsUppercase: 'Mindestens ein Großbuchstabe (A-Z) erforderlich',
  passwordNeedsLowercase: 'Mindestens ein Kleinbuchstabe (a-z) erforderlich',
  passwordNeedsNumber: 'Mindestens eine Zahl (0-9) erforderlich',
  phoneNoLetters: 'Telefonnummer darf keine Buchstaben enthalten',
  phoneNoSpaces: 'Bitte Telefonnummer ohne Leerzeichen eingeben',
  phoneTooShort: 'Telefonnummer ist zu kurz (mindestens {min} Ziffern)',
  phoneTooLong: 'Telefonnummer ist zu lang (maximal {max} Ziffern)',
  postcodeTooShort: 'PLZ muss 5 Ziffern haben (aktuell: {current})',
  postcodeNoLetters: 'PLZ darf nur Ziffern enthalten',
  nameNoNumbers: 'Name darf keine Zahlen enthalten',
  nameInvalidChars: 'Name enthält ungültige Zeichen'
} as const

export const pageTitles = {
  // Bare page names. The 'ITSH | ' prefix is composed in the router's
  // afterEach hook: vue-i18n reads `|` as the plural delimiter, so storing
  // the full title here truncates it to 'ITSH' at render time.
  products: 'Produkte',
  kubernetes: 'Managed Kubernetes',
  objectStorage: 'Object Storage',
  webhosting: 'Webhosting',
  domainSearch: 'Domain registrieren',
  tldCatalog: 'TLD-Übersicht',
  impress: 'Impressum',
  privacy: 'Datenschutz',
  agb: 'AGB',
  sla: 'SLA',
  cancellation: 'Verträge kündigen',
  cancellationConfirm: 'Kündigung bestätigen',
  withdrawal: 'Widerrufsbelehrung',
  nis2: 'NIS2',
  pricingCalculator: 'Preisrechner',
  vserverCompare: 'VServer-Vergleich'
} as const

export const errors = {
  generic: 'Ein Fehler ist aufgetreten. Bitte versuche es später erneut.',
  serviceUnavailable:
    'Der Service ist momentan nicht verfügbar. Bitte versuche es später erneut.',
  gatewayError: 'Der Server ist vorübergehend nicht erreichbar. Bitte versuche es später erneut.',
  networkError: 'Netzwerkfehler. Bitte überprüfe deine Internetverbindung.',
  unauthorized: 'Nicht autorisiert. Bitte melde dich erneut an.',
  forbidden: 'Zugriff verweigert.',
  notFound: 'Die angeforderte Ressource wurde nicht gefunden.',
  conflict: 'Konflikt. Die Ressource existiert bereits.',
  tooManyRequests: 'Zu viele Anfragen. Bitte warte einen Moment.',
  saveFailed: 'Fehler beim Speichern',
  loadFailed: 'Fehler beim Laden',
  deleteFailed: 'Fehler beim Löschen'
} as const
